import React, { FunctionComponent } from 'react';

import Flex from '@gojob/ui-bits/Flex';
import useIsHydrated from '@gojob/ui-bits/hooks/useIsHydrated';
import { Option } from '@gojob/ui-bits/PlacesAutocomplete/useRemoteOptions';
import { ErrorBoundary } from 'react-error-boundary';

import { DEFAULT_RADIUS } from 'src/components/InfiniteJobList/useQueryJobsCount';
import OfferList, { OFFER_LIST_LENGTH } from 'src/components/RelatedOffers/OfferList';
import useRelatedOffers, { SIMILAR_JOBS_RADIUS } from 'src/components/RelatedOffers/useRelatedOffers';
import OfferListTypeProvider from 'src/tracking/OfferListTypeProvider';
import useTranslation from 'src/translations/useTranslation';
import createPath from 'src/utils/create-path';

export type RelatedOffersProps = {
  loading?: boolean;
  keyword?: string;
  city?: Option;
  radius?: number;
  count?: number;
};

export const MAX_OFFERS_THRESHOLD = 10;

const RelatedOffers: FunctionComponent<RelatedOffersProps> = (props) => {
  const { keyword, city, count = MAX_OFFERS_THRESHOLD + 1, radius = DEFAULT_RADIUS } = props;

  const isHydrated = useIsHydrated();
  const t = useTranslation();

  const [lng, lat] = city?.centre?.coordinates || [];
  const skip = props.loading || count > MAX_OFFERS_THRESHOLD || !keyword || !lat || !lng;

  const take = count + OFFER_LIST_LENGTH;

  const { loading, error, nearbyJobsIds, similarJobsIds } = useRelatedOffers(
    keyword,
    lat,
    lng,
    radius,
    take,
    skip
  );

  if (skip || !isHydrated) {
    return null;
  }
  return (
    <ErrorBoundary fallback={null}>
      <Flex flexDirection="column" alignItems="stretch" margin={[4, 0]}>
        <OfferListTypeProvider type="jobboard-similar-offers">
          <OfferList
            to={createPath({ city, keyword, radius: SIMILAR_JOBS_RADIUS, type: 'search' })}
            loading={loading}
            error={!!error}
            jobIds={similarJobsIds}
            title={t('similar_offers_elsewhere')}
            label={t('see_all_similar_offers')}
          />
        </OfferListTypeProvider>

        <OfferListTypeProvider type="jobboard-nearby-offers">
          <OfferList
            to={createPath({ city, radius, type: 'search' })}
            loading={loading}
            error={!!error}
            jobIds={nearbyJobsIds}
            title={t('nearby_offers')}
            label={t('see_all_nearby_offers')}
          />
        </OfferListTypeProvider>
      </Flex>
    </ErrorBoundary>
  );
};

export default RelatedOffers;
